import { useContext, useEffect, useState, Fragment } from 'react'
import { compose } from 'react-recompose'
import { useEventTracker } from '../../utils/hooks'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CloseIcon from '@mui/icons-material/Close'
import LeftIcon from '@mui/icons-material/ChevronLeft'
import RightIcon from '@mui/icons-material/ChevronRight'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MobileStepper from '@mui/material/MobileStepper'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import DialogTitle from '@mui/material/DialogTitle'
import SwipeableViews from 'react-swipeable-views'
import { autoPlay } from 'react-swipeable-views-utils'
import { apolloClient } from '../../graphql/enhancers'
import { StoreContext } from '../../store'
import FormLabel from '@mui/material/FormLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Message from '../Message/Message'
import * as reducers from '../../store/reducers'
import {
  GET_PROSPECT_SGT_APPOINTMENTS,
  REQUEST_SGT_APPOINTMENT,
} from '../../graphql'
import { withApolloProvider } from '../../graphql/enhancers'
import { getImagePrefix } from '@anewgo/utils'
import { getPath } from '@anewgo/functions'
import { EVT_SUBMITTED_REQUEST_SGT_APPOINTMENT_FORM } from '../../constants/eventTracking'
import DateTimePicker from '../DateTimePicker/DateTimePicker'
import clsx from 'clsx'
import Grid from '@mui/material/Grid'
import {
  Root,
  StyledDialog,
  classes,
} from './ProspectSgtAppointmentsDialogStyles'

const AutoPlaySwipeableViews = autoPlay(SwipeableViews)

const ProspectSgtAppointmentsDialog = ({
  client,
  isOpen,
  setOpen,
  agentEmail,
  imgUrl,
  lot,
  community,
  setDialogData = () => {},
}) => {
  const { prospect, dispatch, uiConfig } = useContext(StoreContext)
  const { mobile } = uiConfig
  const [openMessage, setOpenMessage] = useState(false)
  const [modalHide, setModalHide] = useState(false)
  const [activeImageIdx, setActiveImageIdx] = useState(0)
  const track = useEventTracker()
  const clientName = client.altName
  const hasSgtData = lot.inventory.sgtData
  const sgtVendors = getPath('sgtVendors')(client)
  const sgtVendorData =
    hasSgtData &&
    sgtVendors &&
    sgtVendors.find((vendor) => vendor.id === hasSgtData.sgtVendorId)
  const appointmentsEnabled =
    sgtVendorData &&
    getPath('extendedData', 'appointmentsEnabled')(sgtVendorData) !== undefined
      ? getPath('extendedData', 'appointmentsEnabled')(sgtVendorData)
      : true
  const instructionsOnly = getPath(
    'extendedData',
    'instructionsOnly'
  )(sgtVendorData)
  const instructionImages = getPath(
    'extendedData',
    'instructionImages'
  )(sgtVendorData)
  const maxIdx = instructionImages && instructionImages.length - 1
  const autoScrollImages = getPath(
    'extendedData',
    'autoScrollImages'
  )(sgtVendorData)
  const autoScrollInterval =
    getPath('extendedData', 'autoScrollInterval')(sgtVendorData) || 3000
  const maxAppointments =
    getPath('extendedData', 'maxAppointments')(sgtVendorData) || 3
  const inventoryId = lot.inventory.id
  const getAppointments = (clientName, prospectId, inventoryId) => {
    apolloClient
      .query({
        query: GET_PROSPECT_SGT_APPOINTMENTS,
        variables: {
          clientName,
          prospectId,
          inventoryId,
        },
      })
      .then((res) => {
        const appointments = res.data.prospectSgtAppointments.map(
          (appointment) => {
            // Assume that date saved is UTC+0 because that's what our servers save. Adjust accordingly.
            // This will cause localhost development to display incorrect hours. However, this is better
            // than sending the current server time offset through this query to make an accurate
            // adjustment on localhost.
            return (
              appointment.preferredDate + appointment.timezoneOffset * 60000
            )
          }
        )

        let initialPreferredDates = [...appointments]
        initialPreferredDates = initialPreferredDates.sort((a, b) => a - b)
        const maxNewAppointments = maxAppointments - appointments.length
        for (let i = 0; i < maxNewAppointments; i++) {
          initialPreferredDates.push(null)
        }
        setPreferredDates(initialPreferredDates)
      })
  }

  const [preferredDates, setPreferredDates] = useState([])
  const [preferredDatesError, setPreferredDatesError] = useState(false)

  useEffect(() => {
    getAppointments(clientName, prospect.id, inventoryId)
  }, [isOpen])

  const initialName = prospect.appointmentData
    ? prospect.appointmentData.name
    : prospect.name
  const [name, setName] = useState(initialName)
  const [nameError, setNameError] = useState(undefined)

  const initialEmails = prospect.appointmentData
    ? prospect.appointmentData.emails
    : prospect.email
  const [emails, setEmails] = useState(initialEmails)
  const [emailsError, setEmailsError] = useState(undefined)

  const initialPhone = prospect.appointmentData
    ? prospect.appointmentData.phone
    : prospect.phone
  const [phone, setPhone] = useState(initialPhone)
  const [phoneError, setPhoneError] = useState(undefined)
  const [phoneRequired, setPhoneRequired] = useState(false)

  const initialComment = prospect.appointmentData
    ? prospect.appointmentData.comment
    : ''
  const [comment, setComment] = useState(initialComment)

  const initialPreferredContactMethods = prospect.appointmentData
    ? prospect.appointmentData.preferredContactMethods
    : prospect.preferredContactMethods
  const [preferredContactMethods, setPreferredContactMethods] = useState(
    initialPreferredContactMethods
  )

  const validateEmailAddress = (emailAddress) => {
    return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(emailAddress)
  }

  const handleClose = (ev, cancel) => {
    dispatch(
      reducers.setProspect(
        {
          ...prospect,
          appointmentData: {
            name,
            emails,
            phone,
            preferredContactMethods,
            comment,
          },
        },
        null
      )
    )
    if (cancel) {
      setOpen(false)
      setDialogData(null)
      return
    }
    if (!name) {
      setNameError('Please provide your name')
    }
    const emailsArray = emails.split(',')
    let validatedEmails = []
    if (!emails) {
      setEmailsError('Please provide at least one email address')
    } else {
      for (const emailAddress of emailsArray) {
        if (!validateEmailAddress(emailAddress.trim())) {
          setEmailsError(
            'Please provide valid email addresses separated by ", "'
          )
          return
        } else {
          //for some reason emails.trim().split(',') is not trimming the string
          validatedEmails.push(emailAddress.trim())
        }
      }
    }
    if (phoneRequired && !phone) {
      setPhoneError('Please provide your phone number')
    }
    if (nameError || emailsError || phoneError || preferredDatesError) {
      return
    }
    const communityName = community.name.replace(/ /g, '%20')
    const inventoryUrl = `${process.env.REACT_APP_TRUSS}/client/${client.altName}/community/${communityName}/inventory?inventoryId=${inventoryId}&lotId=${lot.id}`
    const timezoneOffset = new Date().getTimezoneOffset()
    const prospectAppointmentData = {
      prospect: {
        id: prospect.id,
        clientId: client.id,
        name,
        email: validatedEmails,
        phone,
        preferredContactMethod: [...new Set(preferredContactMethods)],
        comment,
      },
      inventory: {
        id: inventoryId,
        address: lot.address,
        imgUrl,
        inventoryUrl,
      },
      preferredDate: preferredDates.filter((date) => date != null),
      agentEmail,
    }

    const trackProspectAppointmentData = {
      prospectId: prospect.id,
      clientId: client.id,
      name,
      email: validatedEmails,
      phone,
      preferredContactMethod: [...new Set(preferredContactMethods)],
      comment,
      inventoryId: inventoryId,
      inventoryAddress: lot.address,
      imgUrl,
      inventoryUrl,
      preferredDate: preferredDates.filter((date) => date != null),
      agentEmail,
    }

    track(
      EVT_SUBMITTED_REQUEST_SGT_APPOINTMENT_FORM,
      trackProspectAppointmentData
    )

    apolloClient
      .mutate({
        mutation: REQUEST_SGT_APPOINTMENT,
        refetchQueries: [
          {
            query: GET_PROSPECT_SGT_APPOINTMENTS,
            variables: {
              clientName,
              prospectId: prospect.id,
              inventoryId,
            },
          },
        ],
        variables: {
          clientName,
          prospectSgtApptInput: prospectAppointmentData,
          timezoneOffset,
        },
      })
      .then(() => {
        setOpenMessage(true)
        setModalHide(true)
        setTimeout(() => {
          setOpen(false)
          setDialogData(null)
        }, 4000)
      })
      .catch((error) => {
        console.error('Error submitting appointment request:', error)
      })
  }

  const handleNameChange = (nameInput) => {
    setNameError(undefined)
    setName(nameInput)
  }

  const handleEmailsChange = (emailsInput) => {
    setEmailsError(undefined)
    setEmails(emailsInput)
  }

  const handlePhoneChange = (phoneInput) => {
    setPhone(phoneInput)
  }

  const handleCommentChange = (commentInput) => {
    setComment(commentInput)
  }

  const handleContactMethodsChange = (contactMethodInput) => {
    const updatedContactMethods = preferredContactMethods.includes(
      contactMethodInput
    )
      ? preferredContactMethods.filter(
          (contactMethod) => contactMethod !== contactMethodInput
        )
      : [...preferredContactMethods, contactMethodInput]
    if (
      updatedContactMethods.includes('PHONE') ||
      updatedContactMethods.includes('TEXT')
    ) {
      setPhoneRequired(true)
    } else {
      setPhoneRequired(false)
    }
    setPreferredContactMethods(updatedContactMethods)
  }

  const handlePreferredDatesChange = (value, index) => {
    if (!value.isValid) {
      setPreferredDatesError(true)
    } else {
      setPreferredDatesError(false)
      const updatedPreferredDates = [...preferredDates]
      updatedPreferredDates[index] = value.date ? value.date.valueOf() : null
      setPreferredDates(updatedPreferredDates)
    }
  }

  const renderPreferredDates = () => {
    return (
      <Fragment>
        {preferredDates.map((appointment, i) => (
          <DateTimePicker
            value={appointment ? new Date(appointment).getTime() : null}
            label={`Preferred Date # ${i + 1}`}
            onChange={(value) => handlePreferredDatesChange(value, i)}
          />
        ))}
      </Fragment>
    )
  }

  const prevImageIdx = activeImageIdx === 0 ? null : activeImageIdx - 1
  const nextImageIdx = activeImageIdx === maxIdx ? null : activeImageIdx + 1

  const [slideCompleted, setSlideCompleted] = useState(0)
  const progressJump = instructionImages && 100 / (instructionImages.length - 1)

  const updateImageIdx = (direction) => {
    if (direction === 'forward') {
      setActiveImageIdx(activeImageIdx === maxIdx ? 0 : activeImageIdx + 1)
      setSlideCompleted(slideCompleted + progressJump)
    } else if (direction === 'back') {
      setActiveImageIdx(activeImageIdx === 0 ? maxIdx : activeImageIdx - 1)
      setSlideCompleted(slideCompleted - progressJump)
    } else {
      setActiveImageIdx(0)
      setSlideCompleted(0)
    }
  }
  return (
    <div>
      <StyledDialog
        open={!modalHide ? isOpen : !modalHide}
        onClose={(ev) => handleClose(ev, true)}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        disableEnforceFocus
      >
        <Root className={classes.root}>
          <IconButton
            style={{
              position: 'absolute',
              right: 0,
              top: 4,
              color: 'rgba(0, 0, 0, 0.54)',
            }}
            color="inherit"
            onClick={(ev) => handleClose(ev, true)}
            aria-label="Close"
            size="large"
          >
            <CloseIcon />
          </IconButton>
          {instructionsOnly && (
            <Fragment>
              <DialogTitle id="form-dialog-title">
                <Typography variant="h4" style={{ paddingRight: '24px' }}>
                  Tour this inventory home on your own!
                </Typography>
                {instructionImages && (
                  <AutoPlaySwipeableViews
                    autoplay={autoScrollImages}
                    interval={autoScrollInterval}
                    className={classes.imageContainer}
                    containerStyle={{ height: '66vh' }}
                    enableMouseEvents
                    index={activeImageIdx}
                    onChangeIndex={(index, lastIndex) => {
                      if (index === 0 && lastIndex === maxIdx) {
                        updateImageIdx('first')
                      } else if (index < lastIndex) {
                        updateImageIdx('back')
                      } else {
                        updateImageIdx('forward')
                      }
                    }}
                  >
                    {instructionImages.map((image, idx) => {
                      return (
                        <div
                          key={image + idx}
                          className={classes.instructionImage}
                        >
                          <img
                            src={`${getImagePrefix(
                              null,
                              'sgt_vendors/'
                            )}${image}`}
                            alt={image}
                          />
                        </div>
                      )
                    })}
                  </AutoPlaySwipeableViews>
                )}
                {!mobile && (
                  <Fragment>
                    {prevImageIdx !== null && (
                      <Button
                        className={clsx(
                          classes.navButton,
                          classes.navButtonLeft
                        )}
                        onClick={() => updateImageIdx('back')}
                      >
                        <LeftIcon style={{ fontSize: '2.5rem' }} />
                      </Button>
                    )}
                    {nextImageIdx !== null && (
                      <Button
                        className={clsx(
                          classes.navButton,
                          classes.navButtonRight
                        )}
                        onClick={() => updateImageIdx('forward')}
                      >
                        <RightIcon style={{ fontSize: '2.5rem' }} />
                      </Button>
                    )}
                  </Fragment>
                )}
              </DialogTitle>
              {!mobile && (
                <LinearProgress variant="determinate" value={slideCompleted} />
              )}
              {mobile && instructionImages && (
                <MobileStepper
                  variant="dots"
                  steps={maxIdx + 1}
                  position="static"
                  activeStep={activeImageIdx}
                  className={classes.mobileStepper}
                />
              )}
            </Fragment>
          )}
          {!instructionsOnly && (
            <Fragment>
              <div className={classes.instructionsDialogContainer}>
                <DialogTitle id="form-dialog-title">
                  <Typography
                    variant="h3"
                    style={{ paddingRight: '24px' }}
                  >{`Request ${
                    !!hasSgtData ? 'a Self-Guided Tour' : 'an appointment'
                  }`}</Typography>
                </DialogTitle>
                <div className={classes.instructionsDialogContentSection}>
                  <DialogContent>
                    <Grid container spacing={4}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Name"
                          helperText={nameError}
                          error={!!nameError}
                          type="text"
                          margin="dense"
                          fullWidth
                          required
                          value={name}
                          onChange={(ev) => handleNameChange(ev.target.value)}
                        />
                        <TextField
                          label="Email Addresses"
                          helperText={emailsError}
                          error={!!emailsError}
                          type="text"
                          margin="dense"
                          fullWidth
                          required
                          value={emails}
                          onChange={(ev) => handleEmailsChange(ev.target.value)}
                        />
                        <TextField
                          label="Phone #"
                          helperText={phoneError}
                          error={!!phoneError}
                          type="phone"
                          margin="dense"
                          fullWidth
                          required={phoneRequired}
                          value={phone ? phone : ''}
                          onChange={(ev) => handlePhoneChange(ev.target.value)}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        className={classes.commentInput}
                      >
                        <TextField
                          label="Comment"
                          variant="outlined"
                          type="text"
                          margin="dense"
                          fullWidth
                          rows={7}
                          multiline
                          value={comment}
                          onChange={(ev) =>
                            handleCommentChange(ev.target.value)
                          }
                        />
                      </Grid>
                    </Grid>
                    <FormControl className={classes.formControl}>
                      <FormLabel>Preferred Contact</FormLabel>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={preferredContactMethods.includes(
                                'EMAIL'
                              )}
                              onChange={(ev) =>
                                handleContactMethodsChange(ev.target.name)
                              }
                              name="EMAIL"
                            />
                          }
                          label="EMAIL"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={preferredContactMethods.includes(
                                'PHONE'
                              )}
                              onChange={(ev) =>
                                handleContactMethodsChange(ev.target.name)
                              }
                              name="PHONE"
                            />
                          }
                          label="PHONE"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={preferredContactMethods.includes('TEXT')}
                              onChange={(ev) =>
                                handleContactMethodsChange(ev.target.name)
                              }
                              name="TEXT"
                            />
                          }
                          label="TEXT"
                        />
                      </FormGroup>
                    </FormControl>
                    {appointmentsEnabled && renderPreferredDates()}
                  </DialogContent>
                </div>
                <DialogActions>
                  <Button
                    onClick={(ev) => handleClose(ev, true)}
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={(ev) => handleClose(ev, false)}
                    color="primary"
                  >
                    Send
                  </Button>
                </DialogActions>
              </div>
            </Fragment>
          )}
        </Root>
      </StyledDialog>
      <Message
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMessage}
        autoHideDuration={4000}
        handleClose={() => {
          setOpenMessage('', false)
        }}
        variant="success"
        message="Sent appointment request!"
      />
    </div>
  )
}

const WrappedComponent = compose(withApolloProvider)(
  ProspectSgtAppointmentsDialog
)

export default WrappedComponent
